import { Box, Text, useDisclosure } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'ui-components';
import { PlatformInstagramBorderIcon } from 'ui-components/dist/PlatformInstagramBorderIcon';
import Spinner from '~/components/Spinner';
import {
  CONTENT_TYPES,
  FEATURE_FLAGS,
  SNACKBAR_ERROR_GENERIC_OPTIONS,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { captureException } from '~/errorsLogging';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { selectCreator } from '~/selectors/creator';
import {
  CreatorBenchmarks,
  DefaultMediaPackType,
  InstagramInsightType,
  PostContent,
  ReelsContent,
  StoryContent,
  ValueOf,
} from '~/types';

import { MediaPackDetailSelectContentModal } from '../MediaPackDetailSelectContentModal';
import { UpdateContentSubmitValues } from '../MediaPackDetailSelectContentModal/MediaPackDetailSelectContentModal.types';
import { HighlightedContentSection } from '../MediaPacksDefaultTemplate/HighlightedContentSection';
import { OverviewSection } from '../MediaPacksDefaultTemplate/OverviewSection';
import { mapSubmitDataToUpdateMediaPackValues } from '../MediaPacksDefaultTemplate/OverviewSection/OverviewSection.mapper';
import {
  DEFAULT_INSTAGRAM_METRIC_1,
  DEFAULT_INSTAGRAM_METRIC_2,
  DEFAULT_INSTAGRAM_METRIC_3,
} from './InstagramSection.constants';
import {
  mapInstagramMetricsToMetricCardData,
  mapPostsToHighlightedContentProps,
  mapReelsToHighlightedContentProps,
  mapStoriesToHighlightedContentProps,
  mediaPackContentToIds,
} from './InstagramSection.mappers';

interface Props {
  mediaPack: DefaultMediaPackType;
  creatorBenchmarks: CreatorBenchmarks;
  creatorInsights: InstagramInsightType;
  igPosts: PostContent[];
  igStories: StoryContent[];
  igReels: ReelsContent[];
  onUpdateMediaPack: (payload: UpdateContentSubmitValues) => void;
  isLoading?: boolean;
  error?: Error | null;
  isSharedView?: boolean;
}

export const InstagramSection: React.FC<Props> = ({
  mediaPack,
  creatorBenchmarks,
  creatorInsights,
  igPosts,
  igStories,
  igReels,
  onUpdateMediaPack,
  isSharedView,
  isLoading,
  error,
}) => {
  const [selectedContent, setState] = useState(CONTENT_TYPES.post);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const creator = useSelector(selectCreator);
  const snackbar = useSnackbar();

  const reelMediaPackFlag = useFeatureFlag(FEATURE_FLAGS.foamReelsMediaPack);

  const {
    metric1 = DEFAULT_INSTAGRAM_METRIC_1,
    metric2 = DEFAULT_INSTAGRAM_METRIC_2,
    metric3 = DEFAULT_INSTAGRAM_METRIC_3,
  } = mediaPack?.layout?.metricsPosition?.instagram || {};

  const tempMetrics = metric1.concat(metric2, metric3);
  // TODO: Remove this next line once the backend is updated and updates to the new key igAvgFeedReelsFollowerEngRate
  const metrics = tempMetrics.map((metric) => {
    switch (metric) {
      case 'igAvgFeedFollowerEngRate':
        return 'igAvgFeedReelsFollowerEngRate';
      case 'igAvgFeedEngagements':
        return 'igAvgFeedReelsEngagements';
      case 'igAvgFeedImpressions':
        return 'igAvgFeedStoryImpressions';
      case 'igAvgFeedReach':
        return 'igAvgFeedReelsStoryReach';
      case 'igAvgFeedReachEngRate':
        return 'igAvgFeedReelsReachEngRate';
      default:
        return metric;
    }
  });

  const reducedMetrics = mapInstagramMetricsToMetricCardData(
    metrics,
    creatorBenchmarks,
  );

  const handleSubmit = async (
    selectedContent: PostContent[] | StoryContent[] | ReelsContent[],
    selectedContentType: ValueOf<typeof CONTENT_TYPES>,
    platform: SOCIAL_PLATFORMS,
  ) => {
    const mediaPackValues = mapSubmitDataToUpdateMediaPackValues<
      PostContent | StoryContent | ReelsContent
    >({
      selectedContent,
      selectedContentType,
      platform,
    });

    try {
      await onUpdateMediaPack(mediaPackValues);
      onClose();
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      captureException(error);
    }
  };

  const handleEditPostsContent = () => {
    setState(CONTENT_TYPES.post);

    onOpen();
  };

  const MEDIA_PACK_CONTENT_TYPES = {
    [CONTENT_TYPES.post]: igPosts,
    [CONTENT_TYPES.story]: igStories,
    [CONTENT_TYPES.reels]: igReels,
  };

  const handleEditStoriesContent = () => {
    setState(CONTENT_TYPES.story);

    onOpen();
  };

  const handleEditReelsContent = useCallback(() => {
    setState(CONTENT_TYPES.reels);
    onOpen();
  }, [onOpen, setState]);

  if (reducedMetrics.length === 0) {
    return null;
  }

  if (error) {
    return <Text color="error.500">Something went wrong.</Text>;
  }

  return (
    <Box>
      {!isSharedView && (
        <MediaPackDetailSelectContentModal
          initialSelectedItemIds={mediaPackContentToIds(
            MEDIA_PACK_CONTENT_TYPES[selectedContent],
          )}
          contentType={selectedContent}
          isOpen={isOpen}
          onCancel={onClose}
          onClose={onClose}
          onSubmit={handleSubmit}
          shouldShowSelectionPreview
          creatorId={mediaPack.creator.id}
        />
      )}

      <OverviewSection
        data-testid={`${SOCIAL_PLATFORMS.instagram}-overview-section`}
        overviewTitle="Instagram Overview"
        metrics={reducedMetrics}
        ages={creatorInsights?.ages}
        countries={creatorInsights?.countries?.slice(0, 3)}
        genders={creatorInsights?.genders}
        icon={PlatformInstagramBorderIcon}
        creatorBenchmarks={creatorBenchmarks}
        mediaPack={mediaPack}
        platform={SOCIAL_PLATFORMS.instagram}
        isSharedView={isSharedView}
        insightsLastUpdatedAt={creatorInsights.lastUpdatedAt}
      />

      {isLoading ? (
        <Spinner centered aria-label="Loading content..." />
      ) : (
        <>
          <HighlightedContentSection
            marginTop={14}
            sectionTitle="Instagram Still Highlights"
            contents={mapPostsToHighlightedContentProps({
              hasManager: Boolean(creator?.manager?.id),
              igPosts,
              isSharedView,
            })}
            onEdit={handleEditPostsContent}
            isSharedView={isSharedView}
          />
          <HighlightedContentSection
            marginTop={14}
            sectionTitle="Instagram Stories Highlights"
            contents={mapStoriesToHighlightedContentProps({
              hasManager: Boolean(creator?.manager?.id),
              igStories,
              isSharedView,
            })}
            onEdit={handleEditStoriesContent}
            isSharedView={isSharedView}
          />
          {(reelMediaPackFlag === 'enabled' || isSharedView) && (
            <HighlightedContentSection
              marginTop={14}
              sectionTitle="Instagram Reels Highlights"
              contents={mapReelsToHighlightedContentProps({
                hasManager: Boolean(creator?.manager?.id),
                igReels,
                isSharedView,
              })}
              onEdit={handleEditReelsContent}
              isSharedView={isSharedView}
            />
          )}
        </>
      )}
    </Box>
  );
};
